import { parseAtmTemplateName } from '../services/tradeProcessor';
import { safePnl } from '../utils/formatters';
import { calcTradeStats, calcRStats, calcGroupedStats } from '../services/statsCalculator';

/**
 * Custom hook that provides all setup-related analytics functions.
 * Extracted from TradingJournal.js for modularization.
 *
 * Shared functions (used by both Setups and ATM tabs):
 *   getHourlyStatsForTrades, getRDistributionForTrades,
 *   getSetupPerformanceForAtm, getEdgeScoreForTrades
 */
const useSetupAnalytics = ({ setupAtmBaseTrades, confluences, confluenceCombos, getAllSetupsWithDetails }) => {

  const getSetupStats = (setupName) => {
    const setupTrades = setupAtmBaseTrades.filter(t => t.setup === setupName);
    if (setupTrades.length === 0) return null;

    const stats = calcTradeStats(setupTrades);

    return {
      trades: stats.totalTrades,
      winRate: stats.winRate.toFixed(1),
      totalPnL: stats.totalPnL
    };
  };

  // Get hourly performance breakdown for a set of trades
  const getHourlyStatsForTrades = (tradesList) => {
    const hourlyStats = {};

    tradesList.forEach(trade => {
      const entryTime = trade.entryTime || trade.time;
      if (!entryTime) return;

      let hour;
      if (entryTime.includes(':')) {
        const timeParts = entryTime.split(':');
        hour = parseInt(timeParts[0], 10);
      } else {
        return;
      }

      if (isNaN(hour)) return;

      if (!hourlyStats[hour]) {
        hourlyStats[hour] = {
          hour,
          trades: 0,
          wins: 0,
          losses: 0,
          totalPnL: 0,
          totalR: 0,
          rCount: 0,
          tradesList: []
        };
      }

      hourlyStats[hour].trades++;
      hourlyStats[hour].totalPnL += safePnl(trade);
      hourlyStats[hour].tradesList.push(trade);
      const rVal = parseFloat(trade.rMultiple);
      if (isFinite(rVal) && rVal !== 0) { hourlyStats[hour].totalR += rVal; hourlyStats[hour].rCount++; }

      if (safePnl(trade) > 0) hourlyStats[hour].wins++;
      else if (safePnl(trade) < 0) hourlyStats[hour].losses++;
    });

    const hourlyArray = Object.values(hourlyStats).map(h => ({
      ...h,
      winRate: h.trades > 0 ? (h.wins / h.trades) * 100 : 0,
      avgPnL: h.trades > 0 ? h.totalPnL / h.trades : 0,
      avgR: h.rCount > 0 ? h.totalR / h.rCount : 0
    })).sort((a, b) => a.hour - b.hour);

    return hourlyArray;
  };

  // Get best ATMs for a specific setup
  const getAtmPerformanceForSetup = (setupName) => {
    const setupTrades = setupAtmBaseTrades.filter(t => t.setup === setupName && t.atmStrategy);

    if (setupTrades.length === 0) return [];

    const grouped = calcGroupedStats(setupTrades, t => t.atmStrategy);

    return Object.entries(grouped)
      .map(([name, s]) => ({
        name,
        trades: s.totalTrades,
        wins: s.wins,
        totalPnL: s.totalPnL,
        winRate: s.winRate,
        avgPnL: s.avgPnL
      }))
      .sort((a, b) => b.totalPnL - a.totalPnL);
  };

  // Get best setups for a specific ATM (shared with ATM tab)
  const getSetupPerformanceForAtm = (atmName) => {
    const atmTrades = setupAtmBaseTrades.filter(t => t.atmStrategy === atmName);
    if (atmTrades.length === 0) return [];

    const grouped = calcGroupedStats(atmTrades, t => t.setup || 'Unknown');
    return Object.entries(grouped)
      .map(([name, s]) => ({
        name,
        trades: s.totalTrades,
        wins: s.wins,
        totalPnL: s.totalPnL,
        winRate: s.winRate,
        avgPnL: s.avgPnL
      }))
      .sort((a, b) => b.totalPnL - a.totalPnL);
  };

  // Get confluence performance for a specific setup
  const getConfluencePerformanceForSetup = (setupName) => {
    const setupTrades = setupAtmBaseTrades.filter(t => t.setup === setupName && t.confluences?.length > 0);

    if (setupTrades.length === 0) return { individual: [], recommended: [], topNonRecommended: [], underperformers: [] };

    const setupInfo = getAllSetupsWithDetails().find(s => s.name === setupName);
    const recommendedIds = setupInfo?.recommendedConfluences || [];

    const confStats = {};
    setupTrades.forEach(trade => {
      trade.confluences.forEach(confId => {
        if (!confStats[confId]) {
          confStats[confId] = { id: confId, trades: 0, wins: 0, totalPnL: 0, totalR: 0, rCount: 0 };
        }
        confStats[confId].trades++;
        confStats[confId].totalPnL += safePnl(trade);
        if (safePnl(trade) > 0) confStats[confId].wins++;
        const r = parseFloat(trade.rMultiple);
        if (isFinite(r) && r !== 0) { confStats[confId].totalR += r; confStats[confId].rCount++; }
      });
    });

    const all = Object.values(confStats).map(c => ({
      ...c,
      winRate: c.trades > 0 ? (c.wins / c.trades) * 100 : 0,
      avgPnL: c.trades > 0 ? c.totalPnL / c.trades : 0,
      avgR: c.rCount > 0 ? c.totalR / c.rCount : 0,
      isRecommended: recommendedIds.includes(c.id),
      name: confluences.find(cf => cf.id === c.id)?.name || c.id
    }));

    const recommended = all.filter(c => c.isRecommended).sort((a, b) => b.winRate - a.winRate);
    const nonRecommended = all.filter(c => !c.isRecommended);
    const topNonRecommended = nonRecommended.filter(c => c.winRate >= 55 && c.trades >= 3).sort((a, b) => b.winRate - a.winRate).slice(0, 3);
    const underperformers = all.filter(c => c.winRate < 45 && c.trades >= 3).sort((a, b) => a.winRate - b.winRate).slice(0, 3);

    return { individual: all.sort((a, b) => b.winRate - a.winRate), recommended, topNonRecommended, underperformers };
  };

  // Get confluence COMBINATION performance for a specific setup
  const getComboPerformanceForSetup = (setupName) => {
    const setupTrades = setupAtmBaseTrades.filter(t => t.setup === setupName && t.confluences?.length >= 2);
    if (setupTrades.length === 0) return { autoDiscovered: [], saved: [] };

    const comboStats = {};
    setupTrades.forEach(trade => {
      const confs = trade.confluences;
      const r = parseFloat(trade.rMultiple);
      const hasR = isFinite(r) && r !== 0;
      const isWin = safePnl(trade) > 0;

      // 2-way combinations
      for (let i = 0; i < confs.length; i++) {
        for (let j = i + 1; j < confs.length; j++) {
          const key = [confs[i], confs[j]].sort().join('+');
          if (!comboStats[key]) comboStats[key] = { ids: [confs[i], confs[j]].sort(), trades: 0, wins: 0, totalPnL: 0, totalR: 0, rCount: 0 };
          comboStats[key].trades++;
          comboStats[key].totalPnL += safePnl(trade);
          if (isWin) comboStats[key].wins++;
          if (hasR) { comboStats[key].totalR += r; comboStats[key].rCount++; }
        }
      }

      // 3-way combinations
      if (confs.length >= 3) {
        for (let i = 0; i < confs.length; i++) {
          for (let j = i + 1; j < confs.length; j++) {
            for (let k = j + 1; k < confs.length; k++) {
              const key = [confs[i], confs[j], confs[k]].sort().join('+');
              if (!comboStats[key]) comboStats[key] = { ids: [confs[i], confs[j], confs[k]].sort(), trades: 0, wins: 0, totalPnL: 0, totalR: 0, rCount: 0 };
              comboStats[key].trades++;
              comboStats[key].totalPnL += safePnl(trade);
              if (isWin) comboStats[key].wins++;
              if (hasR) { comboStats[key].totalR += r; comboStats[key].rCount++; }
            }
          }
        }
      }
    });

    const autoDiscovered = Object.entries(comboStats)
      .map(([key, c]) => ({
        key,
        ids: c.ids,
        names: c.ids.map(id => confluences.find(cf => cf.id === id)?.name || id),
        trades: c.trades,
        wins: c.wins,
        winRate: c.trades > 0 ? (c.wins / c.trades) * 100 : 0,
        avgR: c.rCount > 0 ? c.totalR / c.rCount : 0,
        avgPnL: c.trades > 0 ? c.totalPnL / c.trades : 0,
        totalPnL: c.totalPnL,
        isSaved: false,
        comboName: null
      }))
      .filter(c => c.trades >= 2)
      .sort((a, b) => b.winRate - a.winRate || b.trades - a.trades)
      .slice(0, 6);

    const saved = confluenceCombos.map(combo => {
      const matchingTrades = setupAtmBaseTrades.filter(t =>
        t.setup === setupName &&
        t.confluences &&
        combo.confluenceIds.every(id => t.confluences.includes(id))
      );
      const s = calcTradeStats(matchingTrades);
      const r = calcRStats(matchingTrades);

      return {
        key: combo.confluenceIds.sort().join('+'),
        ids: combo.confluenceIds,
        names: combo.confluenceIds.map(id => confluences.find(cf => cf.id === id)?.name || id),
        trades: s.totalTrades,
        wins: s.wins,
        winRate: s.winRate,
        avgR: r.avgR,
        avgPnL: s.avgPnL,
        totalPnL: s.totalPnL,
        isSaved: true,
        comboName: combo.name,
        comboId: combo.id
      };
    });

    const savedKeys = new Set(saved.map(s => s.key));
    const filteredAuto = autoDiscovered.filter(a => !savedKeys.has(a.key));

    return { autoDiscovered: filteredAuto, saved };
  };

  // Get top confluences for a specific Setup + ATM combination
  const getConfluencePerformanceForSetupAndAtm = (setupName, atmName) => {
    const trades = setupAtmBaseTrades.filter(t =>
      t.setup === setupName &&
      t.atmStrategy === atmName &&
      t.confluences?.length > 0
    );

    if (trades.length === 0) return [];

    const confStats = {};
    trades.forEach(trade => {
      trade.confluences.forEach(confId => {
        if (!confStats[confId]) {
          confStats[confId] = { id: confId, trades: 0, wins: 0, totalPnL: 0 };
        }
        confStats[confId].trades++;
        confStats[confId].totalPnL += safePnl(trade);
        if (safePnl(trade) > 0) confStats[confId].wins++;
      });
    });

    return Object.values(confStats)
      .map(c => ({
        ...c,
        winRate: c.trades > 0 ? (c.wins / c.trades) * 100 : 0,
        name: confluences.find(cf => cf.id === c.id)?.name || c.id
      }))
      .filter(c => c.trades >= 2)
      .sort((a, b) => b.winRate - a.winRate)
      .slice(0, 3);
  };

  // Calculate edge score for a set of trades (in R terms)
  const getEdgeScoreForTrades = (tradesList) => {
    const tradesWithR = tradesList.filter(t => {
      const r = parseFloat(t.rMultiple);
      return isFinite(r) && r !== 0;
    });
    if (tradesWithR.length === 0) return 0;
    const winners = tradesWithR.filter(t => parseFloat(t.rMultiple) > 0);
    const losers = tradesWithR.filter(t => parseFloat(t.rMultiple) < 0);
    const winRate = tradesWithR.length > 0 ? winners.length / tradesWithR.length : 0;
    const avgWinR = winners.length > 0 ? winners.reduce((sum, t) => sum + parseFloat(t.rMultiple), 0) / winners.length : 0;
    const avgLossR = losers.length > 0 ? Math.abs(losers.reduce((sum, t) => sum + parseFloat(t.rMultiple), 0) / losers.length) : 0;
    return (winRate * avgWinR) - ((1 - winRate) * avgLossR);
  };

  // Get R-Multiple distribution for a set of trades
  const getRDistributionForTrades = (tradesList) => {
    const tradesWithR = tradesList.filter(t => {
      const r = parseFloat(t.rMultiple);
      return isFinite(r) && r !== 0;
    });
    const total = tradesList.length;
    const withR = tradesWithR.length;
    if (withR === 0) return { total, withR: 0, data: [], avgWinR: 0, avgLossR: 0, edgeScore: 0 };

    const rValues = tradesWithR.map(t => parseFloat(t.rMultiple));
    const winners = rValues.filter(r => r > 0);
    const losers = rValues.filter(r => r < 0);
    const avgWinR = winners.length > 0 ? winners.reduce((a, b) => a + b, 0) / winners.length : 0;
    const avgLossR = losers.length > 0 ? Math.abs(losers.reduce((a, b) => a + b, 0) / losers.length) : 0;

    const thresholds = [
      { label: '≥1R', min: 1 },
      { label: '≥2R', min: 2 },
      { label: '≥3R', min: 3 },
      { label: '≥4R', min: 4 }
    ];
    const data = thresholds.map(({ label, min }) => {
      const count = rValues.filter(r => r >= min).length;
      return { threshold: label, count, pct: withR > 0 ? (count / withR) * 100 : 0 };
    });

    return { total, withR, data, avgWinR, avgLossR, edgeScore: getEdgeScoreForTrades(tradesList) };
  };

  // Get stop loss size breakdown for a setup
  const getStopLossBreakdownForSetup = (setupName) => {
    const setupTrades = setupAtmBaseTrades.filter(t => t.setup === setupName);
    if (setupTrades.length === 0) return [];

    const slGroups = {};
    setupTrades.forEach(trade => {
      let slSize = parseFloat(trade.stopLossPoints);
      if (!isFinite(slSize) || slSize <= 0) {
        const parsed = parseAtmTemplateName(trade.atmStrategy);
        slSize = parsed.stopLossPoints;
      }
      if (!slSize || slSize <= 0) return;
      const key = Math.round(slSize);
      if (!slGroups[key]) {
        slGroups[key] = { slSize: key, trades: [], wins: 0, losses: 0, totalPnL: 0, totalR: 0 };
      }
      slGroups[key].trades.push(trade);
      slGroups[key].totalPnL += safePnl(trade);
      const r = parseFloat(trade.rMultiple);
      if (isFinite(r)) slGroups[key].totalR += r;
      if (safePnl(trade) > 0) slGroups[key].wins++;
      else if (safePnl(trade) < 0) slGroups[key].losses++;
    });

    return Object.values(slGroups)
      .map(g => {
        const tradeCount = g.trades.length;
        const tradesWithR = g.trades.filter(t => { const r = parseFloat(t.rMultiple); return isFinite(r) && r !== 0; });
        return {
          slSize: g.slSize,
          trades: tradeCount,
          wins: g.wins,
          losses: g.losses,
          winRate: tradeCount > 0 ? (g.wins / tradeCount) * 100 : 0,
          totalPnL: g.totalPnL,
          avgPnL: tradeCount > 0 ? g.totalPnL / tradeCount : 0,
          avgR: tradesWithR.length > 0 ? g.totalR / tradesWithR.length : 0,
          rDist: getRDistributionForTrades(g.trades),
          edgeScore: getEdgeScoreForTrades(g.trades)
        };
      })
      .filter(g => g.trades >= 2)
      .sort((a, b) => b.edgeScore - a.edgeScore);
  };

  // Get direction split (long vs short) for a setup
  const getDirectionSplitForSetup = (setupName) => {
    const setupTrades = setupAtmBaseTrades.filter(t => t.setup === setupName);
    const buildDirStats = (trades) => {
      const s = calcTradeStats(trades);
      const r = calcRStats(trades);
      return {
        trades: s.totalTrades,
        wins: s.wins,
        losses: s.losses,
        winRate: s.winRate,
        totalPnL: s.totalPnL,
        avgPnL: s.avgPnL,
        avgR: r.avgR,
        rDist: getRDistributionForTrades(trades),
        edgeScore: getEdgeScoreForTrades(trades)
      };
    };
    return {
      long: buildDirStats(setupTrades.filter(t => t.direction === 'long')),
      short: buildDirStats(setupTrades.filter(t => t.direction === 'short'))
    };
  };

  // Get counter-confluence impact for a setup
  const getCounterConfluenceImpactForSetup = (setupName) => {
    const setupTrades = setupAtmBaseTrades.filter(t => t.setup === setupName);
    if (setupTrades.length === 0) return { withCC: null, withoutCC: null, individual: [], delta: null };

    const withCC = setupTrades.filter(t => t.counterConfluences && t.counterConfluences.length > 0);
    const withoutCC = setupTrades.filter(t => !t.counterConfluences || t.counterConfluences.length === 0);

    const calcGroup = (trades) => {
      if (trades.length === 0) return null;
      const s = calcTradeStats(trades);
      const r = calcRStats(trades);
      return {
        trades: s.totalTrades,
        wins: s.wins,
        winRate: s.winRate,
        avgR: r.avgR
      };
    };

    const ccStats = {};
    withCC.forEach(trade => {
      trade.counterConfluences.forEach(ccId => {
        if (!ccStats[ccId]) {
          ccStats[ccId] = { id: ccId, trades: 0, wins: 0, totalR: 0, rCount: 0 };
        }
        ccStats[ccId].trades++;
        if (safePnl(trade) > 0) ccStats[ccId].wins++;
        const r = parseFloat(trade.rMultiple);
        if (isFinite(r) && r !== 0) { ccStats[ccId].totalR += r; ccStats[ccId].rCount++; }
      });
    });

    const individual = Object.values(ccStats)
      .map(c => ({
        ...c,
        winRate: c.trades > 0 ? (c.wins / c.trades) * 100 : 0,
        avgR: c.rCount > 0 ? c.totalR / c.rCount : 0,
        name: confluences.find(cf => cf.id === c.id)?.name || c.id
      }))
      .filter(c => c.trades >= 2)
      .sort((a, b) => a.winRate - b.winRate);

    const withCCStats = calcGroup(withCC);
    const withoutCCStats = calcGroup(withoutCC);
    const delta = (withCCStats && withoutCCStats) ? {
      wrDelta: withCCStats.winRate - withoutCCStats.winRate,
      rDelta: withCCStats.avgR - withoutCCStats.avgR
    } : null;

    return { withCC: withCCStats, withoutCC: withoutCCStats, individual, delta };
  };

  return {
    getSetupStats,
    getHourlyStatsForTrades,
    getAtmPerformanceForSetup,
    getSetupPerformanceForAtm,
    getConfluencePerformanceForSetup,
    getComboPerformanceForSetup,
    getConfluencePerformanceForSetupAndAtm,
    getEdgeScoreForTrades,
    getRDistributionForTrades,
    getStopLossBreakdownForSetup,
    getDirectionSplitForSetup,
    getCounterConfluenceImpactForSetup
  };
};

export default useSetupAnalytics;
